import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import {
  FileText, Download, Mail, X, Loader2, Save, Check, CheckSquare, Square,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, ChevronRight,
  Archive, Paperclip, Search, Tag, ShieldAlert
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import {
  getTemplates, generateDocument, downloadDocx, saveDocument, emailDocument,
  batchEmailDocuments, batchDownloadDocuments, saveDocxToRecord
} from '../../api/documentTemplates';

export default function GenerateDocumentModal({ recordId, recordTypeId, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [step, setStep] = useState('select');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [phiBlocked, setPhiBlocked] = useState(false);

  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [activeDocIndex, setActiveDocIndex] = useState(0);

  // Email form
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailMode, setEmailMode] = useState('attachments'); // 'attachments' | 'zip'
  const [sending, setSending] = useState(false);

  const [savingAll, setSavingAll] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Multi-tag filter
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagLogic, setTagLogic] = useState('OR');

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] px-4 py-3',
      },
    },
  });

  const prevIndexRef = useRef(0);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const { data } = await getTemplates({ record_type_id: recordTypeId });
      setTemplates(data.map(t => ({ ...t, tags: typeof t.tags === 'string' ? JSON.parse(t.tags) : (t.tags || []) })));
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentEditorContent = () => {
    if (editor && generatedDocs.length > 0) {
      const html = editor.getHTML();
      setGeneratedDocs(prev => {
        const updated = [...prev];
        if (updated[prevIndexRef.current]) {
          updated[prevIndexRef.current] = { ...updated[prevIndexRef.current], html };
        }
        return updated;
      });
    }
  };

  const switchToDoc = (index) => {
    saveCurrentEditorContent();
    prevIndexRef.current = index;
    setActiveDocIndex(index);
    if (editor && generatedDocs[index]) {
      editor.commands.setContent(generatedDocs[index].html);
    }
  };

  const toggleTemplate = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const targetIds = filteredTemplates.map(t => t.id);
    const allSelected = targetIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); targetIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); targetIds.forEach(id => next.add(id)); return next; });
    }
  };

  const handleGenerateSelected = async () => {
    const selected = templates.filter(t => selectedIds.has(t.id));
    if (selected.length === 0) { toast.error('Select at least one template'); return; }
    setGenerating(true);
    const docs = [];
    let failed = 0;
    for (const template of selected) {
      try {
        const { data } = await generateDocument(template.id, recordId);
        if (data.template_type === 'docx' || data.docx_base64) {
          docs.push({ templateId: template.id, templateName: data.template_name || template.name, isDocx: true, docxBase64: data.docx_base64, previewHtml: data.preview_html || '' });
        } else {
          docs.push({ templateId: template.id, templateName: data.template_name || template.name, html: data.html });
        }
      } catch (err) { failed++; }
    }
    if (docs.length === 0) { toast.error('All documents failed to generate'); setGenerating(false); return; }
    if (failed > 0) toast(`${failed} failed, ${docs.length} generated`, { icon: '⚠️' });
    setGeneratedDocs(docs);
    setActiveDocIndex(0);
    prevIndexRef.current = 0;
    setEmailSubject(docs.length === 1 ? `Document: ${docs[0].templateName}` : `${docs.length} Documents`);
    if (editor && docs[0] && !docs[0].isDocx) editor.commands.setContent(docs[0].html);
    setStep('edit');
    setGenerating(false);
  };

  const handleGenerateSingle = async (template) => {
    setSelectedIds(new Set([template.id]));
    setGenerating(true);
    try {
      const { data } = await generateDocument(template.id, recordId);
      let doc;
      if (data.template_type === 'docx' || data.docx_base64) {
        doc = { templateId: template.id, templateName: data.template_name || template.name, isDocx: true, docxBase64: data.docx_base64, previewHtml: data.preview_html || '' };
      } else {
        doc = { templateId: template.id, templateName: data.template_name || template.name, html: data.html };
      }
      const docs = [doc];
      setGeneratedDocs(docs);
      setActiveDocIndex(0);
      prevIndexRef.current = 0;
      setEmailSubject(`Document: ${docs[0].templateName}`);
      if (editor && !doc.isDocx) editor.commands.setContent(docs[0].html);
      setStep('edit');
    } catch (err) {
      toast.error('Failed to generate: ' + (err.response?.data?.error || err.message));
    } finally { setGenerating(false); }
  };

  const getCurrentHtml = () => editor?.getHTML() || generatedDocs[activeDocIndex]?.html || '';

  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download single docx
  const handleDownloadCurrent = async () => {
    const doc = generatedDocs[activeDocIndex];
    if (!doc) return;

    if (doc.isDocx) {
      // DOCX template — download from base64 in memory (no second API call)
      if (doc.docxBase64) {
        const bytes = Uint8Array.from(atob(doc.docxBase64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        triggerBlobDownload(blob, `${doc.templateName.replace(/[^a-zA-Z0-9]/g, '_')}.docx`);
        toast.success('Downloaded!');
      } else {
        try {
          const { data } = await downloadDocx(doc.templateId, recordId, {});
          triggerBlobDownload(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }),
            `${doc.templateName.replace(/[^a-zA-Z0-9]/g, '_')}.docx`);
          toast.success('Downloaded!');
        } catch (err) { toast.error('Download failed'); }
      }
    } else {
      // HTML template — existing flow
      saveCurrentEditorContent();
      try {
        const { data } = await downloadDocx(doc.templateId, recordId, { html: getCurrentHtml(), template_name: doc.templateName });
        triggerBlobDownload(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }),
          `${doc.templateName.replace(/[^a-zA-Z0-9]/g, '_')}.docx`);
        toast.success('Downloaded!');
      } catch (err) { toast.error('Download failed'); }
    }
  };

  // Download all as ZIP of docx
  const handleDownloadAll = async () => {
    saveCurrentEditorContent();
    setDownloadingAll(true);
    try {
      const { data } = await batchDownloadDocuments({
        documents: generatedDocs.map(d => ({ html: d.html, template_name: d.templateName }))
      });
      triggerBlobDownload(new Blob([data], { type: 'application/zip' }), `Documents_${Date.now()}.zip`);
      toast.success(`Downloaded ${generatedDocs.length} documents as ZIP`);
    } catch (err) {
      toast.error('Download failed');
    }
    setDownloadingAll(false);
  };

  // Save single document to record
  const handleSaveCurrent = async () => {
    const doc = generatedDocs[activeDocIndex];
    if (!doc) return;
    try {
      if (doc.isDocx) {
        if (doc.docxBase64) {
          const filename = `${doc.templateName.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.docx`;
          await saveDocxToRecord(doc.templateId, { record_id: recordId, docx_base64: doc.docxBase64, filename });
        } else {
          await saveDocument(doc.templateId, recordId, {});
        }
        toast.success(`Saved "${doc.templateName}" as DOCX`);
      } else {
        saveCurrentEditorContent();
        await saveDocument(doc.templateId, recordId, { html: getCurrentHtml(), template_name: doc.templateName });
        toast.success(`Saved "${doc.templateName}" as PDF`);
      }
    } catch (err) { toast.error('Save failed'); }
  };

  // Save all documents to record
  const handleSaveAll = async () => {
    saveCurrentEditorContent();
    setSavingAll(true);
    let success = 0;
    for (const doc of generatedDocs) {
      try {
        if (doc.isDocx) {
          if (doc.docxBase64) {
            const filename = `${doc.templateName.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.docx`;
            await saveDocxToRecord(doc.templateId, { record_id: recordId, docx_base64: doc.docxBase64, filename });
          } else {
            await saveDocument(doc.templateId, recordId, {});
          }
        } else {
          await saveDocument(doc.templateId, recordId, { html: doc.html, template_name: doc.templateName });
        }
        success++;
      } catch (err) { console.error(`Save failed for ${doc.templateName}:`, err); }
    }
    toast.success(`Saved ${success} of ${generatedDocs.length} documents to record`);
    setSavingAll(false);
    if (success === generatedDocs.length) onClose();
  };

  // Email — single or batch
  const handleEmail = async () => {
    if (!emailTo) { toast.error('Enter a recipient email'); return; }
    setSending(true);
    saveCurrentEditorContent();

    try {
      if (generatedDocs.length === 1) {
        // Single doc — use original endpoint
        const doc = generatedDocs[0];
        const emailPayload = {
          recipient_email: emailTo, subject: emailSubject,
          message: emailMessage || '<p>Please find the attached document.</p>'
        };
        if (!doc.isDocx) {
          emailPayload.html = doc.html;
          emailPayload.template_name = doc.templateName;
        }
        await emailDocument(doc.templateId, recordId, emailPayload);
      } else {
        // Multiple — use batch endpoint
        await batchEmailDocuments({
          documents: generatedDocs.map(d => ({
            template_id: d.templateId, record_id: recordId,
            html: d.html, template_name: d.templateName
          })),
          recipient_email: emailTo,
          subject: emailSubject,
          message: emailMessage || '<p>Please find the attached document(s).</p>',
          mode: emailMode
        });
      }
      toast.success(`${generatedDocs.length} document(s) emailed to ${emailTo}`);
      onClose();
    } catch (err) {
      if (err.response?.data?.error === 'phi_blocked') {
        setPhiBlocked(true);
      } else {
        toast.error('Email failed: ' + (err.response?.data?.error || err.message));
      }
    }
    setSending(false);
  };

  // Filter templates by text search + selected tags
  const filteredTemplates = templates.filter(t => {
    // Text search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchesText = t.name.toLowerCase().includes(q) ||
        (t.description && t.description.toLowerCase().includes(q)) ||
        (t.tags && t.tags.some(tag => tag.name.toLowerCase().includes(q)));
      if (!matchesText) return false;
    }
    // Tag filter
    if (selectedTags.length > 0) {
      const tTagNames = (t.tags || []).map(tag => tag.name.toLowerCase());
      if (tagLogic === 'AND') {
        if (!selectedTags.every(st => tTagNames.includes(st.toLowerCase()))) return false;
      } else {
        if (!selectedTags.some(st => tTagNames.includes(st.toLowerCase()))) return false;
      }
    }
    return true;
  });

  const activeDoc = generatedDocs[activeDocIndex];
  const multiMode = generatedDocs.length > 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold">
              {step === 'select' && 'Generate Documents'}
              {step === 'edit' && (multiMode ? `Editing ${generatedDocs.length} Documents` : `Edit: ${activeDoc?.templateName}`)}
              {step === 'email' && `Email ${generatedDocs.length} Document${generatedDocs.length > 1 ? 's' : ''}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Select */}
          {step === 'select' && (
            <div className="p-6">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No templates available for this record type</p>
                  <p className="text-gray-400 text-sm mt-1">Create templates in Admin → Templates</p>
                </div>
              ) : (
                <>
                  {/* Search bar */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search by name or tag..."
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      autoFocus
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* All unique tags as multi-select filter pills */}
                  {(() => {
                    const allTags = [];
                    const seen = new Set();
                    templates.forEach(t => (t.tags || []).forEach(tag => {
                      const key = tag.name.toLowerCase();
                      if (!seen.has(key)) { seen.add(key); allTags.push(tag); }
                    }));
                    if (allTags.length === 0) return null;
                    return (
                      <div className="flex items-center gap-1.5 mb-4">
                        <div className="flex flex-wrap items-center gap-1.5 flex-1">
                          {allTags.map(tag => {
                            const isActive = selectedTags.some(st => st.toLowerCase() === tag.name.toLowerCase());
                            return (
                              <button key={tag.id} onClick={() => {
                                setSelectedTags(prev =>
                                  isActive
                                    ? prev.filter(st => st.toLowerCase() !== tag.name.toLowerCase())
                                    : [...prev, tag.name]
                                );
                              }}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition ${
                                  isActive
                                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                }`}>
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: tag.color }} />
                                {tag.name}
                              </button>
                            );
                          })}
                          {selectedTags.length > 0 && (
                            <button onClick={() => setSelectedTags([])}
                              className="text-[11px] text-gray-400 hover:text-gray-600 ml-1">
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden flex-shrink-0">
                          <button onClick={() => setTagLogic('OR')}
                            className={`px-2 py-0.5 text-[10px] font-semibold transition ${
                              tagLogic === 'OR' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}>OR</button>
                          <button onClick={() => setTagLogic('AND')}
                            className={`px-2 py-0.5 text-[10px] font-semibold border-l border-gray-200 transition ${
                              tagLogic === 'AND' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}>AND</button>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <button onClick={selectAll} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                        {filteredTemplates.length > 0 && filteredTemplates.every(t => selectedIds.has(t.id))
                          ? <CheckSquare className="w-4 h-4 text-blue-600" />
                          : <Square className="w-4 h-4" />}
                        {filteredTemplates.length > 0 && filteredTemplates.every(t => selectedIds.has(t.id)) ? 'Deselect All' : 'Select All'}
                      </button>
                      {(searchQuery || selectedTags.length > 0) && (
                        <span className="text-xs text-gray-400">{filteredTemplates.length} of {templates.length} templates</span>
                      )}
                    </div>
                    {selectedIds.size > 1 && (
                      <button onClick={handleGenerateSelected} disabled={generating}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Generate {selectedIds.size} Documents
                      </button>
                    )}
                  </div>

                  {filteredTemplates.length === 0 && (searchQuery || selectedTags.length > 0) ? (
                    <div className="text-center py-8">
                      <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">
                        No templates match{searchQuery ? ` "${searchQuery}"` : ''}{searchQuery && selectedTags.length > 0 ? ' with ' : ''}{selectedTags.length > 0 ? `tag${selectedTags.length > 1 ? 's' : ''}: ${selectedTags.join(', ')}` : ''}
                      </p>
                    </div>
                  ) : (
                  <div className="space-y-2">
                    {filteredTemplates.map(template => {
                      const isSelected = selectedIds.has(template.id);
                      return (
                        <div key={template.id}
                          className={`flex items-center gap-3 p-4 border rounded-xl transition ${isSelected ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <button onClick={() => toggleTemplate(template.id)} className="flex-shrink-0">
                            {isSelected ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-300" />}
                          </button>
                          <button onClick={() => toggleTemplate(template.id)} className="flex-1 min-w-0 text-left">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{template.name}</p>
                              {template.template_type === 'docx' && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-teal-50 text-teal-700 rounded">DOCX</span>
                              )}
                            </div>
                            {template.description && <p className="text-sm text-gray-500 truncate">{template.description}</p>}
                            {template.tags && template.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {template.tags.map(tag => (
                                  <span key={tag.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full"
                                    style={{ background: tag.color + '20', color: tag.color }}>
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: tag.color }} />
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {template.placeholders?.length || 0} fields •
                              Updated {new Date(template.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </p>
                          </button>
                          <button onClick={() => handleGenerateSingle(template)} disabled={generating}
                            className="flex-shrink-0 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-50"
                            title="Generate this one only">
                            {generating && selectedIds.size === 1 && selectedIds.has(template.id)
                              ? <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                              : <ChevronRight className="w-5 h-5" />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  )}
                  {selectedIds.size === 1 && (
                    <div className="mt-4 flex justify-end">
                      <button onClick={handleGenerateSelected} disabled={generating}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Generate Document
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 2: Edit */}
          {step === 'edit' && (
            <div>
              {multiMode && (
                <div className="bg-gray-50 border-b px-4 py-2 flex items-center gap-2 overflow-x-auto">
                  {generatedDocs.map((doc, i) => (
                    <button key={doc.templateId} onClick={() => switchToDoc(i)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                        i === activeDocIndex ? 'bg-white shadow text-blue-700 border border-gray-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
                      <FileText className="w-3.5 h-3.5" />
                      {doc.templateName}
                      {doc.isDocx && <span className="text-[10px] px-1 py-0.5 bg-teal-50 text-teal-600 rounded">DOCX</span>}
                    </button>
                  ))}
                </div>
              )}

              {activeDoc?.isDocx ? (
                /* DOCX document — server-rendered mammoth HTML preview */
                <div className="flex flex-col" style={{ minHeight: '400px' }}>
                  <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    Preview is approximate. Download the .docx for exact formatting.
                  </div>
                  {activeDoc.previewHtml ? (
                    <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: '60vh' }}>
                      <div
                        className="max-w-3xl mx-auto bg-white shadow-sm border border-gray-200 rounded-lg px-10 py-8"
                        style={{ fontFamily: "'Times New Roman', Times, serif", lineHeight: '1.6', fontSize: '14px' }}
                        dangerouslySetInnerHTML={{ __html: activeDoc.previewHtml }}
                      />
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center py-16 text-gray-400">
                      <FileText className="w-10 h-10 mb-3" />
                      <p className="text-sm">Preview not available. Download the .docx to view.</p>
                    </div>
                  )}
                </div>
              ) : editor ? (
                /* HTML document — TinyMCE editor */
                <>
                  <div className="bg-gray-50 border-b px-4 py-1.5 flex items-center gap-1">
                    <ToolbarBtn icon={<Bold className="w-4 h-4" />} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
                    <ToolbarBtn icon={<Italic className="w-4 h-4" />} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
                    <ToolbarBtn icon={<UnderlineIcon className="w-4 h-4" />} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
                    <div className="w-px h-5 bg-gray-200 mx-1" />
                    <ToolbarBtn icon={<List className="w-4 h-4" />} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
                    <ToolbarBtn icon={<ListOrdered className="w-4 h-4" />} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
                  </div>
                  <div className="max-w-3xl mx-auto py-4">
                    <EditorContent editor={editor} />
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Step 3: Email */}
          {step === 'email' && (
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                  placeholder="client@email.com" autoFocus
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)}
                  placeholder="Please find the attached document(s)." rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>

              {/* Attachment mode toggle — only for multi-doc */}
              {multiMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attachment Format</label>
                  <div className="flex gap-3">
                    <button onClick={() => setEmailMode('attachments')}
                      className={`flex-1 flex items-center gap-3 p-3 border-2 rounded-xl transition ${
                        emailMode === 'attachments' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <Paperclip className={`w-5 h-5 ${emailMode === 'attachments' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${emailMode === 'attachments' ? 'text-blue-700' : 'text-gray-700'}`}>
                          Separate PDFs
                        </p>
                        <p className="text-xs text-gray-500">{generatedDocs.length} individual attachments</p>
                      </div>
                    </button>
                    <button onClick={() => setEmailMode('zip')}
                      className={`flex-1 flex items-center gap-3 p-3 border-2 rounded-xl transition ${
                        emailMode === 'zip' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <Archive className={`w-5 h-5 ${emailMode === 'zip' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${emailMode === 'zip' ? 'text-blue-700' : 'text-gray-700'}`}>
                          Single ZIP
                        </p>
                        <p className="text-xs text-gray-500">All PDFs bundled in one file</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* File list */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-600 mb-1">
                  {multiMode
                    ? `${generatedDocs.length} documents will be ${emailMode === 'zip' ? 'zipped and ' : ''}attached:`
                    : 'Document attached:'}
                </p>
                <ul className="text-sm text-gray-700 space-y-0.5">
                  {generatedDocs.map(doc => (
                    <li key={doc.templateId} className="flex items-center gap-1.5">
                      <FileText className={`w-3.5 h-3.5 ${doc.isDocx ? 'text-teal-500' : 'text-red-500'}`} />
                      {doc.templateName}.{doc.isDocx ? 'docx' : 'pdf'}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'select' && (
          <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
            <button onClick={() => { if (step === 'edit') saveCurrentEditorContent(); setStep(step === 'email' ? 'edit' : 'select'); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800">
              ← Back
            </button>
            <div className="flex gap-2">
              {step === 'edit' && (
                <>
                  {multiMode ? (
                    <button onClick={handleSaveAll} disabled={savingAll}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 text-sm">
                      {savingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save All to Record
                    </button>
                  ) : (
                    <button onClick={handleSaveCurrent}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 text-sm">
                      <Save className="w-4 h-4" /> Save {activeDoc?.isDocx ? 'as DOCX' : 'as PDF'}
                    </button>
                  )}
                  {multiMode ? (
                    <button onClick={handleDownloadAll} disabled={downloadingAll}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 text-sm">
                      {downloadingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                      Download ZIP
                    </button>
                  ) : (
                    <button onClick={handleDownloadCurrent}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 text-sm">
                      <Download className="w-4 h-4" /> Download .docx
                    </button>
                  )}
                  <button onClick={() => { if (!activeDoc?.isDocx) saveCurrentEditorContent(); setStep('email'); }}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                    <Mail className="w-4 h-4" /> Email{multiMode ? ' All' : ''} to Client
                  </button>
                </>
              )}
              {step === 'email' && (
                <button onClick={handleEmail} disabled={sending || !emailTo}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {sending ? 'Sending...' : `Send ${generatedDocs.length > 1 ? generatedDocs.length + ' Documents' : 'Email'}`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* PHI Blocked Dialog */}
      {phiBlocked && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={() => setPhiBlocked(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-red-50 px-6 py-4 flex items-center gap-3 border-b border-red-100">
              <ShieldAlert className="h-6 w-6 text-red-600 flex-shrink-0" />
              <h3 className="text-lg font-bold text-red-900">Cannot Send — PHI Detected</h3>
            </div>
            <div className="px-6 py-5 text-sm text-gray-700 space-y-3">
              <p>This document contains protected health information (PHI). Your current email configuration uses the platform email provider, which is <strong>not HIPAA compliant</strong> for PHI transmission.</p>
              <p>To email documents containing PHI, your firm administrator must configure one of these options in <strong>Admin &rarr; Email Configuration</strong>:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Connected Email</strong> — Connect your firm's Gmail or Outlook account</li>
                <li><strong>BYOSMTP</strong> — Connect your firm's own email server</li>
              </ul>
              <p className="text-gray-500 text-xs">These options ensure PHI is transmitted through your firm's own HIPAA-compliant email infrastructure.</p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-2">
              {user?.is_admin && (
                <button
                  onClick={() => { setPhiBlocked(false); onClose(); navigate('/admin/email-settings'); }}
                  className="text-sm bg-brand-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-brand-700"
                >
                  Go to Email Settings
                </button>
              )}
              <button onClick={() => setPhiBlocked(false)} className="text-sm bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolbarBtn({ icon, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`p-1.5 rounded ${active ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>
      {icon}
    </button>
  );
}
